import {
    createEditor,
    createToolbar,
    type IDomEditor,
    type IEditorConfig,
    type IToolbarConfig,
    type Toolbar
} from "@wangeditor/editor";
import "@wangeditor/editor/dist/css/style.css";
import { forwardRef, useEffect, useImperativeHandle, useRef, type ReactNode } from "react";

import { fileService } from "@/api/services/fileService";
import "./index.scss";

export interface RichEditorRef {
    editor: {
        getElemsByType: (type: "image" | "video") => Array<{ src: string }>;
    };
    files: Map<string, number>;
}

export interface WangEditorProps {
    value?: string;
    height?: string;
    mode?: "default" | "simple";
    hideToolBar?: boolean;
    disabled?: boolean;
    placeholder?: string;
    toolbarConfig?: Partial<IToolbarConfig>;
    editorConfig?: Partial<IEditorConfig>;
    onUpdateValue?: (value: string) => void;
    onCheckValidate?: () => void;
    children?: ReactNode;
}

const IMAGE_URL = import.meta.env.VITE_API_URL;
const baseFileUrl = `${IMAGE_URL}/dataService/admin/file/download-with-sign`;

const DEFAULT_TOOLBAR_CONFIG: Partial<IToolbarConfig> = {
    excludeKeys: ["code", "emotion", "sub", "sup", "codeBlock", "insertTable", "todo", "insertImage", "insertVideo"]
};
const DEFAULT_EDITOR_CONFIG: Partial<IEditorConfig> = {};

/** React 版 WangEditor 直接挂载与 Vue 旧版相同的 v5 core，避免手写工具栏产生视觉和能力偏差。 */
const WangEditor = forwardRef<RichEditorRef, WangEditorProps>(function WangEditor(
    {
        value = "",
        height = "500px",
        mode = "default",
        hideToolBar = false,
        disabled = false,
        placeholder = "请输入内容...",
        toolbarConfig = DEFAULT_TOOLBAR_CONFIG,
        editorConfig = DEFAULT_EDITOR_CONFIG,
        onUpdateValue,
        onCheckValidate,
        children
    },
    ref
) {
    const toolbarContainerRef = useRef<HTMLDivElement>(null);
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<IDomEditor | null>(null);
    const toolbarRef = useRef<Toolbar | null>(null);
    const filesRef = useRef(new Map<string, number>());
    const updateValueRef = useRef(onUpdateValue);
    const validateRef = useRef(onCheckValidate);
    const suppressNextChangeRef = useRef(true);

    useEffect(() => {
        updateValueRef.current = onUpdateValue;
        validateRef.current = onCheckValidate;
    }, [onCheckValidate, onUpdateValue]);

    useImperativeHandle(
        ref,
        () => ({
            editor: {
                getElemsByType: type =>
                    (editorRef.current?.getElemsByType(type) as Array<{ src: string }> | undefined) || []
            },
            files: filesRef.current
        }),
        []
    );

    useEffect(() => {
        const editorContainer = editorContainerRef.current;
        const toolbarContainer = toolbarContainerRef.current;
        if (!editorContainer || (!hideToolBar && !toolbarContainer)) return;

        /** 复用旧版上传校验和预签名接口，把媒体插入编辑器并记录文件 ID。 */
        const uploadMedia = async (file: File, type: "image" | "video", insert: (url: string) => void) => {
            const validType =
                type === "image"
                    ? ["image/jpeg", "image/png", "image/gif"].includes(file.type)
                    : ["video/mp4", "video/quicktime"].includes(file.type);
            const validSize = file.size / 1024 / 1024 < (type === "image" ? 5 : 200);
            if (!validType || !validSize) return;

            const formData = new FormData();
            formData.append("file", file);
            const data = await fileService.uploadFilePreSign(formData);
            const url = data.path || `${baseFileUrl}/${data.id}`;
            filesRef.current.set(url, data.id);
            insert(url);
        };

        const menuConfig = {
            ...(editorConfig.MENU_CONF || {}),
            uploadImage: {
                ...(editorConfig.MENU_CONF?.uploadImage || {}),
                customUpload: (file: File, insertFn: (url: string) => void) => uploadMedia(file, "image", insertFn)
            },
            uploadVideo: {
                ...(editorConfig.MENU_CONF?.uploadVideo || {}),
                customUpload: (file: File, insertFn: (url: string) => void) => uploadMedia(file, "video", insertFn)
            }
        };

        const editor = createEditor({
            selector: editorContainer,
            html: value,
            mode,
            config: {
                autoFocus: false,
                ...editorConfig,
                readOnly: disabled,
                placeholder,
                MENU_CONF: menuConfig,
                onChange: currentEditor => {
                    // 初始化和外部 setHtml 会触发 change，只同步真实编辑，避免把纯文本回填改写成 <p>。
                    if (suppressNextChangeRef.current) {
                        suppressNextChangeRef.current = false;
                        return;
                    }
                    const html = currentEditor.isEmpty() ? "" : currentEditor.getHtml();
                    updateValueRef.current?.(html);
                    editorConfig.onChange?.(currentEditor);
                },
                onBlur: currentEditor => {
                    validateRef.current?.();
                    editorConfig.onBlur?.(currentEditor);
                }
            }
        });
        editorRef.current = editor;

        if (!hideToolBar && toolbarContainer) {
            toolbarRef.current = createToolbar({ editor, selector: toolbarContainer, config: toolbarConfig, mode });
        }

        return () => {
            toolbarRef.current?.destroy();
            toolbarRef.current = null;
            editor.destroy();
            editorRef.current = null;
        };
        // 编辑器实例只在配置边界变化时重建；外部 value 由下方独立 effect 同步。
    }, [disabled, editorConfig, hideToolBar, mode, placeholder, toolbarConfig]);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor || editor.getHtml() === value) return;
        suppressNextChangeRef.current = true;
        editor.setHtml(value || "");
    }, [value]);

    return (
        <div className={`editor-box ${disabled ? "editor-disabled" : ""}`} data-mode={mode}>
            {!hideToolBar ? <div ref={toolbarContainerRef} className="editor-toolbar" /> : null}
            {children}
            <div ref={editorContainerRef} className="editor-content" style={{ height }} />
        </div>
    );
});

export default WangEditor;
